import React, { useState, useEffect, useRef } from 'react';
import { documentApi } from '../api';
import PermissionModal from '../components/common/PermissionModal';
import './DocumentList.css';

const DocumentList = () => {
    const [documents, setDocuments] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({ title: '', content: '', doc_type: 'normal', is_public: false });
    const [user, setUser] = useState(null);
    const [permissionModalVisible, setPermissionModalVisible] = useState(false);

    // Tab and Filter state
    const [activeTab, setActiveTab] = useState('notice'); // Default to Announcement
    const [keyword, setKeyword] = useState('');
    const [myOwnOnly, setMyOwnOnly] = useState(false);

    const editorRef = useRef(null);

    useEffect(() => {
        const u = JSON.parse(localStorage.getItem('user'));
        setUser(u);
    }, []);

    // Fetch documents when tab or filters change
    useEffect(() => {
        if (user) {
            fetchDocuments();
        }
    }, [user, activeTab, myOwnOnly]);

    useEffect(() => {
        if (isEditing && editorRef.current) {
            editorRef.current.innerHTML = editForm.content;
        }
    }, [isEditing]);

    const fetchDocuments = async () => {
        try {
            const params = {
                doc_type: activeTab,
                keyword: keyword
            };
            const res = await documentApi.getList(params);
            if (res.code === 200) {
                let data = res.data;
                // Client side filter for "My Own" in normal tab
                if (activeTab === 'normal' && myOwnOnly && user) {
                    data = data.filter(d => d.created_by === user.id);
                }
                setDocuments(data);

                // Refresh selected document if it's in the current list
                if (selectedDoc) {
                    const updated = data.find(d => d.id === selectedDoc.id);
                    if (updated) setSelectedDoc(updated);
                    else if (selectedDoc.id !== 'new') setSelectedDoc(null);
                }
            }
        } catch (error) {
            console.error('Failed to fetch documents', error);
        }
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            fetchDocuments();
        }
    };

    const handleSelect = (doc) => {
        if (isEditing) {
            if (!window.confirm('当前正在编辑，切换将丢失未保存的内容，确认切换？')) return;
        }
        setSelectedDoc(doc);
        setIsEditing(false);
    };

    const handleCreate = () => {
        const newDoc = {
            title: '新文档',
            content: '在此输入内容...',
            doc_type: activeTab === 'notice' ? 'notice' : 'normal',
            is_public: activeTab === 'notice'
        };
        setSelectedDoc(null);
        setEditForm(newDoc);
        setIsEditing(true);
        setSelectedDoc({ id: 'new', ...newDoc });
    };

    const handleEdit = (doc) => {
        setEditForm({
            title: doc.title,
            content: doc.content,
            doc_type: doc.doc_type,
            is_public: doc.is_public
        });
        setIsEditing(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('确认删除该文档？')) return;
        try {
            await documentApi.delete(id);
            fetchDocuments();
            if (selectedDoc && selectedDoc.id === id) {
                setSelectedDoc(null);
                setIsEditing(false);
            }
        } catch (error) {
            alert('删除失败: ' + error.message);
        }
    };

    const handleSave = async () => {
        const content = editorRef.current.innerHTML;
        const { title, doc_type, is_public } = editForm;

        if (!title.trim()) {
            alert('请输入标题');
            return;
        }

        try {
            let res;
            if (selectedDoc.id === 'new') {
                res = await documentApi.create({ title, content, doc_type, is_public });
            } else {
                res = await documentApi.update(selectedDoc.id, { title, content, doc_type, is_public });
            }

            if (res.code === 200) {
                setIsEditing(false);
                await fetchDocuments();
                setSelectedDoc(res.data);
            }
        } catch (error) {
            alert('保存失败: ' + error.message);
        }
    };

    const execCmd = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current.focus();
    };

    const isAdmin = user && user.role === 'admin';
    const isOwner = user && selectedDoc && selectedDoc.created_by === user.id;
    const canEdit = isAdmin || isOwner || (selectedDoc && selectedDoc.can_edit);
    const canShare = isAdmin || isOwner;

    const getPermissionLabel = (doc) => {
        if (!user) return '';
        if (user.role === 'admin') return '管理员';
        if (doc.created_by === user.id) return '所有者';
        if (doc.permission_level === 'write') return '可编辑';
        if (doc.permission_level === 'read') return '只读';
        if (doc.doc_type === 'notice') return '公共公告';
        return '';
    };

    return (
        <div className="doc-container">
            <div className="doc-sidebar">
                <div className="doc-sidebar-header">
                    <h3>文档中心</h3>
                    {isAdmin && activeTab === 'notice' && (
                        <button className="add-btn" onClick={handleCreate} title="发布公告">+</button>
                    )}
                    {activeTab === 'normal' && (
                        <button className="add-btn" onClick={handleCreate} title="创建文档">+</button>
                    )}
                </div>

                <div className="doc-tabs">
                    <div
                        className={`tab-item ${activeTab === 'notice' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('notice'); setSelectedDoc(null); setIsEditing(false); }}
                    >
                        系统公告
                    </div>
                    <div
                        className={`tab-item ${activeTab === 'normal' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('normal'); setSelectedDoc(null); setIsEditing(false); }}
                    >
                        个人文档
                    </div>
                </div>

                <div className="doc-filters">
                    <input
                        type="text"
                        placeholder="搜索标题..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={handleSearch}
                    />
                    {activeTab === 'normal' && (
                        <label className="own-only">
                            <input
                                type="checkbox"
                                checked={myOwnOnly}
                                onChange={(e) => setMyOwnOnly(e.target.checked)}
                            />
                            仅自己
                        </label>
                    )}
                </div>

                <div className="doc-list">
                    {documents.map(doc => (
                        <div
                            key={doc.id}
                            className={`doc-item ${selectedDoc && selectedDoc.id === doc.id ? 'active' : ''}`}
                            onClick={() => handleSelect(doc)}
                        >
                            <div className="doc-item-title">{doc.title}</div>
                            <div className="doc-item-meta">
                                <span>{doc.created_by_name}</span>
                                <span>{new Date(doc.updated_at).toLocaleDateString()}</span>
                            </div>
                            {getPermissionLabel(doc) && (
                                <div className="doc-item-tag">{getPermissionLabel(doc)}</div>
                            )}
                        </div>
                    ))}
                    {selectedDoc && selectedDoc.id === 'new' && (
                        <div className="doc-item active">
                            <div className="doc-item-title">新文档...</div>
                        </div>
                    )}
                    {documents.length === 0 && !selectedDoc && (
                        <div className="empty-list">暂无内容</div>
                    )}
                </div>
            </div>

            <div className="doc-content">
                {selectedDoc ? (
                    <>
                        {isEditing ? (
                            <div className="doc-editor">
                                <div className="editor-toolbar">
                                    <div className="title-row">
                                        <input
                                            type="text"
                                            className="title-input"
                                            value={editForm.title}
                                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                            placeholder="文档标题"
                                        />
                                        {isAdmin && (
                                            <label className="type-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={editForm.doc_type === 'notice'}
                                                    onChange={(e) => setEditForm({ ...editForm, doc_type: e.target.checked ? 'notice' : 'normal' })}
                                                />
                                                设为公告
                                            </label>
                                        )}
                                        <label className="public-toggle">
                                            <input
                                                type="checkbox"
                                                checked={editForm.is_public}
                                                onChange={(e) => setEditForm({ ...editForm, is_public: e.target.checked })}
                                            />
                                            公开访问
                                        </label>
                                    </div>
                                    <div className="toolbar-buttons">
                                        <div className="tool-group">
                                            <button title="加粗" onMouseDown={(e) => { e.preventDefault(); execCmd('bold'); }}><b>B</b></button>
                                            <button title="斜体" onMouseDown={(e) => { e.preventDefault(); execCmd('italic'); }}><i>I</i></button>
                                            <button title="下划线" onMouseDown={(e) => { e.preventDefault(); execCmd('underline'); }}><u>U</u></button>
                                            <button title="删除线" onMouseDown={(e) => { e.preventDefault(); execCmd('strikeThrough'); }}><strike>S</strike></button>
                                        </div>
                                        <div className="tool-group">
                                            <button title="一级标题" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'H1'); }}>H1</button>
                                            <button title="二级标题" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'H2'); }}>H2</button>
                                            <button title="三级标题" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'H3'); }}>H3</button>
                                        </div>
                                        <div className="tool-group">
                                            <button title="无序列表" onMouseDown={(e) => { e.preventDefault(); execCmd('insertUnorderedList'); }}>UL</button>
                                            <button title="有序列表" onMouseDown={(e) => { e.preventDefault(); execCmd('insertOrderedList'); }}>OL</button>
                                            <button title="分割线" onMouseDown={(e) => { e.preventDefault(); execCmd('insertHorizontalRule'); }}>HR</button>
                                        </div>
                                        <div className="tool-group">
                                            <button title="左对齐" onMouseDown={(e) => { e.preventDefault(); execCmd('justifyLeft'); }}>L</button>
                                            <button title="居中对齐" onMouseDown={(e) => { e.preventDefault(); execCmd('justifyCenter'); }}>C</button>
                                            <button title="右对齐" onMouseDown={(e) => { e.preventDefault(); execCmd('justifyRight'); }}>R</button>
                                        </div>
                                        <div className="tool-group">
                                            <input
                                                type="color"
                                                title="字体颜色"
                                                onChange={(e) => execCmd('foreColor', e.target.value)}
                                                className="color-picker"
                                            />
                                            <input
                                                type="color"
                                                title="背景颜色"
                                                defaultValue="#ffffff"
                                                onChange={(e) => execCmd('hiliteColor', e.target.value)}
                                                className="color-picker bg-picker"
                                            />
                                            <button title="清除格式" onMouseDown={(e) => { e.preventDefault(); execCmd('removeFormat'); }}>&empty;</button>
                                        </div>
                                        <div className="action-group">
                                            <button onClick={handleSave} className="save-btn">保存</button>
                                            <button onClick={() => { setIsEditing(false); if (selectedDoc.id === 'new') setSelectedDoc(null); }} className="cancel-btn">取消</button>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="editor-area"
                                    contentEditable
                                    ref={editorRef}
                                ></div>
                            </div>
                        ) : (
                            <div className="doc-viewer">
                                <div className="viewer-header">
                                    <div className="header-main">
                                        <h2>{selectedDoc.title}</h2>
                                        <div className="badge-row">
                                            {selectedDoc.doc_type === 'notice' && <span className="badge notice">公告</span>}
                                            {selectedDoc.is_public && <span className="badge public-label">公开</span>}
                                            <span className="badge perm">{getPermissionLabel(selectedDoc)}</span>
                                        </div>
                                    </div>
                                    <div className="viewer-actions">
                                        {canShare && (
                                            <button onClick={() => setPermissionModalVisible(true)} className="share-btn">共享权限</button>
                                        )}
                                        {canEdit && (
                                            <button onClick={() => handleEdit(selectedDoc)}>编辑内容</button>
                                        )}
                                        {(isAdmin || isOwner) && (
                                            <button onClick={() => handleDelete(selectedDoc.id)} className="delete-btn">删除文档</button>
                                        )}
                                    </div>
                                </div>
                                <div className="viewer-info-bar">
                                    <div className="info-item">
                                        <span className="label">创建者:</span>
                                        <span className="value">{selectedDoc.created_by_name || '系统'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">创建时间:</span>
                                        <span className="value">{new Date(selectedDoc.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">最后更新:</span>
                                        <span className="value">{selectedDoc.updated_by_name || '系统'} ({new Date(selectedDoc.updated_at).toLocaleString()})</span>
                                    </div>
                                </div>
                                <div className="viewer-body" dangerouslySetInnerHTML={{ __html: selectedDoc.content }}></div>
                            </div>
                        )}

                        <PermissionModal
                            visible={permissionModalVisible}
                            onClose={() => setPermissionModalVisible(false)}
                            resourceType="sys_document"
                            resourceId={selectedDoc.id}
                            resourceTitle={selectedDoc.title}
                        />
                    </>
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">📂</div>
                        <h3>欢迎来到文档中心</h3>
                        <p>请从左侧选择一个文档查看，或搜索您需要的内容。</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentList;
